import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, CheckCircle2, Camera, X, Loader2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SMS_CONSENT_TEXT =
  "I agree to receive marketing text messages from FlipPilot LLC about selling my property, potential cash offers, and related home-selling services. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our Terms and Privacy Policy.";

export default function Contact() {
  useSEO({
    title: "Contact FlipPilot. Request Your Cash Offer",
    description:
      "Send us your address and situation. You'll get a fair, no-obligation cash offer within 24 hours. Serving Cape Coral, Fort Myers, Naples, and all of Southwest Florida.",
    path: "/contact",
    keywords:
      "contact cash home buyer Florida, get cash offer house, sell house fast contact",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    timeline: "",
    situation: "",
    message: "",
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState([]);   // [{name,size,data}]
  const [photoBusy, setPhotoBusy] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const onPhotoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // let them re-pick same file if they delete it
    if (!files.length) return;
    if (photos.length + files.length > 5) {
      toast.error("Max 5 photos");
      return;
    }
    setPhotoBusy(true);
    try {
      const added = [];
      for (const f of files) {
        if (!f.type.startsWith("image/")) {
          toast.error(`${f.name} isn't an image, skipped`);
          continue;
        }
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name} is over 5 MB, skipped`);
          continue;
        }
        const data = await readFileAsDataUrl(f);
        added.push({ name: f.name, size: f.size, data });
      }
      setPhotos((prev) => [...prev, ...added]);
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = (idx) =>
    setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.email && !form.phone)) {
      toast.error("Name plus email or phone are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketing/leads`, {
        ...form,
        photos,
        sms_consent: smsConsent,
        sms_consent_text: smsConsent ? SMS_CONSENT_TEXT : "",
      });
      setDone(true);
      toast.success("Thanks. We'll be in touch within a business day.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-16" data-testid="contact-page">
      <div className="mkt-container grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2">
          <span className="mkt-chip">
            <MapPin size={12} />
            Southwest Florida
          </span>
          <h1 className="mkt-hero-title mt-6 leading-none">
            Tell us about the house.
          </h1>
          <p className="mt-5 text-[var(--mkt-text-muted)] leading-relaxed">
            Fill out the form or call us directly. You&rsquo;ll get a fair, written
            cash offer within one business day. No obligation, ever.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 grid place-items-center rounded-full shrink-0"
                style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
              >
                <Phone size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--mkt-text-muted)]">Phone</div>
                <a
                  href="tel:+12394808400"
                  className="font-semibold hover:text-[var(--mkt-accent-dark)]"
                  data-testid="contact-phone"
                >
                  (239) 480-8400
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 grid place-items-center rounded-full shrink-0"
                style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
              >
                <Mail size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--mkt-text-muted)]">Email</div>
                <a
                  href="mailto:sales@flippilot.us"
                  className="font-semibold hover:text-[var(--mkt-accent-dark)]"
                  data-testid="contact-email"
                >
                  sales@flippilot.us
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 grid place-items-center rounded-full shrink-0"
                style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
              >
                <MapPin size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--mkt-text-muted)]">Service Area</div>
                <div className="font-semibold">Cape Coral · Fort Myers · Naples · SWFL</div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          {done ? (
            <div className="mkt-card p-10 text-center" data-testid="contact-success">
              <CheckCircle2 size={48} className="mx-auto" style={{ color: "var(--mkt-accent)" }} />
              <h2 className="mkt-section-title mt-4">Thanks. We&rsquo;ve got it.</h2>
              <p className="mt-3 text-[var(--mkt-text-muted)] max-w-md mx-auto">
                A member of the team will reach out within one business day.
                If you need a same-day response, please call us directly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mkt-card p-8" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your Name *" testid="contact-name"
                       value={form.name} onChange={update("name")} required />
                <Field label="Phone" testid="contact-phone-input" type="tel"
                       value={form.phone} onChange={update("phone")}
                       placeholder="(239) 555-0123" />
                <Field label="Email" testid="contact-email-input" type="email"
                       value={form.email} onChange={update("email")}
                       placeholder="you@example.com" />
                <Field label="Property Address" testid="contact-address"
                       value={form.address} onChange={update("address")}
                       placeholder="123 Main St, Cape Coral, FL" />
                <Field label="Timeline"
                       select
                       options={["ASAP (within 30 days)", "1-3 months", "Just exploring options"]}
                       testid="contact-timeline"
                       value={form.timeline} onChange={update("timeline")} />
                <Field label="Situation"
                       select
                       options={["Inherited property", "Tired landlord", "Pre-foreclosure",
                                 "Divorce", "Relocation", "Downsizing", "Other"]}
                       testid="contact-situation"
                       value={form.situation} onChange={update("situation")} />
              </div>

              <label
                className="mt-4 flex items-start gap-3 cursor-pointer"
                data-testid="contact-sms-consent-label"
              >
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  data-testid="contact-sms-consent-checkbox"
                  className="mt-1 h-4 w-4 shrink-0 rounded border-[var(--mkt-line)] accent-[var(--mkt-accent)] cursor-pointer"
                />
                <span className="text-xs leading-relaxed text-[var(--mkt-text-muted)]">
                  I agree to receive marketing text messages from FlipPilot LLC
                  about selling my property, potential cash offers, and related
                  home-selling services. Message frequency varies. Message and
                  data rates may apply. Reply STOP to opt out or HELP for help.
                  Consent is not a condition of purchase. See our{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer"
                     className="underline hover:text-[var(--mkt-accent)]">Terms</a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer"
                     className="underline hover:text-[var(--mkt-accent)]">Privacy Policy</a>.
                </span>
              </label>

              <div className="mt-4">
                <label className="block text-xs uppercase tracking-widest text-[var(--mkt-text-muted)] mb-2">
                  Anything else we should know?
                </label>
                <textarea
                  data-testid="contact-message"
                  className="w-full border border-[var(--mkt-line)] rounded-lg px-3 py-2.5 focus:outline-none focus:border-[var(--mkt-accent)] transition-colors min-h-[100px]"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Condition of the house, tenants, timing constraints, etc."
                />
              </div>

              {/* ---- Photos (optional) ---- */}
              <div className="mt-4" data-testid="contact-photos-section">
                <label className="block text-xs uppercase tracking-widest text-[var(--mkt-text-muted)] mb-2">
                  Photos of the house{" "}
                  <span className="normal-case tracking-normal text-[var(--mkt-text-muted)]/70">
                    (optional — helps us give a better offer)
                  </span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map((p, i) => (
                    <div key={i} className="relative rounded-lg overflow-hidden border border-[var(--mkt-line)] aspect-square bg-[var(--mkt-surface)]"
                         data-testid={`contact-photo-thumb-${i}`}>
                      <img src={p.data} alt={p.name}
                           className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removePhoto(i)}
                              data-testid={`contact-photo-remove-${i}`}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600 transition">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {photos.length < 5 && (
                    <label className={`aspect-square border-2 border-dashed border-[var(--mkt-line)] rounded-lg flex flex-col items-center justify-center gap-1.5 cursor-pointer hover:border-[var(--mkt-accent)] hover:bg-[var(--mkt-accent)]/5 transition ${photoBusy ? "opacity-50 pointer-events-none" : ""}`}
                           data-testid="contact-photo-upload-tile">
                      <input type="file" accept="image/*" multiple
                             onChange={onPhotoPick} className="hidden"
                             data-testid="contact-photo-input" />
                      {photoBusy ? (
                        <Loader2 size={22} className="text-[var(--mkt-text-muted)] animate-spin" />
                      ) : (
                        <Camera size={22} className="text-[var(--mkt-text-muted)]" />
                      )}
                      <div className="text-[10px] uppercase tracking-widest text-[var(--mkt-text-muted)]">
                        {photos.length === 0 ? "Add photos" : "Add more"}
                      </div>
                    </label>
                  )}
                </div>
                <div className="text-[10px] text-[var(--mkt-text-muted)] mt-2">
                  Max 5 photos, up to 5 MB each. JPG, PNG, HEIC.
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mkt-btn mkt-btn-primary mt-6 w-full justify-center"
              >
                <Send size={16} />
                {submitting ? "Sending…" : "Request My Cash Offer"}
              </button>
              <p className="text-[10px] uppercase tracking-widest text-[var(--mkt-text-muted)] mt-4 text-center">
                No obligation · Response within 1 business day · Info stays private
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, required, select, options }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[var(--mkt-text-muted)] mb-2">
        {label}
      </label>
      {select ? (
        <select
          data-testid={testid}
          value={value}
          onChange={onChange}
          className="w-full border border-[var(--mkt-line)] rounded-lg px-3 py-2.5 focus:outline-none focus:border-[var(--mkt-accent)] transition-colors bg-white"
        >
          <option value="">- select -</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full border border-[var(--mkt-line)] rounded-lg px-3 py-2.5 focus:outline-none focus:border-[var(--mkt-accent)] transition-colors"
        />
      )}
    </div>
  );
}
